import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import * as globalActions from '@actions/global';
import { fetchUserStatus } from '@api/global';
import {
  userDataSelector,
  authenticationTokenSelector,
  userServiceSelector,
  userStatusSelector,
} from '@selectors/global';
import useLogout from '@hooks/useLogout';
import ErrorMessage from '../login/ErrorMessage';

const AuthorizedWrapper = ({
  authenticationToken,
  children,
  userData,
  userService,
  userStatus,
  updateUserStatusFromOutside,
}) => {
  const intervalRef = useRef();
  const inputEl = useRef(null);
  const [errorMessage, setErrorMessage] = useState('');
  const userStatusJS = userStatus.toJS();
  const { logout } = useLogout();

  useEffect(() => {
    fetchStatus();
    startTimer();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const startTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    const id = setInterval(() => fetchStatus(), 15000);
    intervalRef.current = id;
  };

  const clearIntervalAndLogout = async () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    logout();
  };

  const fetchStatus = async () => {
    const userID = userData.toJS().id;
    if (userID) {
      try {
        const res = await fetchUserStatus(
          { userService },
          authenticationToken,
          {
            userID,
          },
        );
        // Force user to log in again if forceLogOff is true
        if (res?.results[0]) {
          if (res.results[0].forceLogOff) {
            clearIntervalAndLogout();
          }
          if (inputEl.current) {
            const data = JSON.parse(inputEl.current.dataset.value);
            if (
              data.forceLogOff !== res.results[0].forceLogOff ||
              data.readOnly !== res.results[0].readOnly
            ) {
              updateUserStatusFromOutside(res);
            }
          }
        }
      } catch (error) {
        console.error(error);
        const message = `Cannot log in to user service: ${
          error?.message || error
        }`;
        setErrorMessage(message);
      }
    }
  };

  return (
    <div ref={inputEl} data-value={JSON.stringify(userStatusJS)}>
      {errorMessage ? <ErrorMessage message={errorMessage} /> : children}
    </div>
  );
};

AuthorizedWrapper.propTypes = {
  updateUserStatus: PropTypes.func.isRequired,
  updateUserStatusFromOutside: PropTypes.func.isRequired,
  authenticationToken: PropTypes.any,
  userService: PropTypes.any,
  children: PropTypes.any,
  userData: PropTypes.any,
  userStatus: PropTypes.any,
};

const mapDispatchToProps = {
  updateUserStatus: globalActions.updateUserStatus,
  updateUserStatusFromOutside: globalActions.updateUserStatusFromOutside,
};

const mapStateToProps = createStructuredSelector({
  userData: userDataSelector,
  authenticationToken: authenticationTokenSelector,
  userService: userServiceSelector,
  userStatus: userStatusSelector,
});

export default connect(mapStateToProps, mapDispatchToProps)(AuthorizedWrapper);
